import { Building2, UserRound } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { getOrganizationCalendarSchedules } from "lib/api/organizationCalendar";

import {
  CalendarWeekScheduleBars,
  getDefaultScheduleCalendarColorStyle,
} from "./OrganizationCalendar";

const WEEKDAY_LABELS = ["일", "월", "화", "수", "목", "금", "토"];

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfWeek(date) {
  return addDays(startOfDay(date), -date.getDay());
}

function formatDateKey(date) {
  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-");
}

function formatHeaderDate(date) {
  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join(".");
}

export default function OrganizationSummaryWeekCalendar({ organizationId }) {
  const weekDays = useMemo(() => {
    const weekStart = startOfWeek(new Date());
    return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  }, []);
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const weekStart = weekDays[0];
  const weekEnd = weekDays[weekDays.length - 1];
  const weekRangeLabel = `${formatHeaderDate(weekStart)} ~ ${formatHeaderDate(
    weekEnd,
  )}`;
  const todayKey = formatDateKey(new Date());

  useEffect(() => {
    let isActive = true;

    async function loadSchedules() {
      if (!organizationId) {
        setSchedules([]);
        setError("조직 정보를 확인할 수 없습니다.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError("");

      const result = await getOrganizationCalendarSchedules({
        organizationId,
        from: weekStart.toISOString(),
        to: addDays(weekStart, 7).toISOString(),
      });

      if (!isActive) return;

      setIsLoading(false);
      if (!result.success) {
        setSchedules([]);
        setError(result.message || "주간 일정을 불러오지 못했습니다.");
        return;
      }

      setSchedules(
        Array.isArray(result.data?.schedules) ? result.data.schedules : [],
      );
    }

    loadSchedules();

    return () => {
      isActive = false;
    };
  }, [organizationId, weekStart]);

  return (
    <article className="organizationPage__panel organizationPage__panel--full organizationSummaryWeek">
      <div className="organizationPage__panelHeader organizationSummaryWeek__header">
        <h3>주간 일정</h3>
        <span>{weekRangeLabel}</span>
        <div
          aria-label="일정 아이콘 범례"
          className="organizationSummaryWeek__scopeLegend"
          role="list"
        >
          <span role="listitem">
            <Building2 aria-hidden="true" size={12} strokeWidth={2.2} />
            조직 일정
          </span>
          <span role="listitem">
            <UserRound aria-hidden="true" size={12} strokeWidth={2.2} />
            개인 일정
          </span>
        </div>
      </div>

      {error && (
        <p className="organizationPage__panelNote isError">{error}</p>
      )}

      <div
        aria-busy={isLoading}
        className="organizationCalendar__grid organizationCalendar__grid--summary isWeek"
      >
        {WEEKDAY_LABELS.map((weekday, index) => (
          <div
            className={[
              "organizationCalendar__weekday",
              index === 0 ? "isSunday" : "",
              index === 6 ? "isSaturday" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            key={weekday}
          >
            {weekday}
          </div>
        ))}

        {weekDays.map((day) => {
          const dateKey = formatDateKey(day);

          return (
            <div
              className={[
                "organizationCalendar__day",
                day.getDay() === 0 ? "isSunday" : "",
                day.getDay() === 6 ? "isSaturday" : "",
                dateKey === todayKey ? "isToday" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              key={dateKey}
              style={{
                gridColumn: day.getDay() + 1,
                gridRow: 2,
              }}
            >
              <span className="organizationCalendar__dayHeader">
                <span className="organizationCalendar__dayDate">
                  <span>{day.getDate()}</span>
                </span>
                <span className="organizationCalendar__dayHeaderActions">
                  {dateKey === todayKey && <small>오늘</small>}
                </span>
              </span>
            </div>
          );
        })}

        <CalendarWeekScheduleBars
          days={weekDays}
          isLoading={isLoading}
          isReadOnly
          resolveScheduleColorStyle={getDefaultScheduleCalendarColorStyle}
          schedules={schedules}
        />
      </div>
    </article>
  );
}
